import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Slider,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { GroupedOffer } from "../types";
import api from "../../../../api/axios";
import { toast } from "react-toastify";

// debounce helper
const debounce = (fn: (...args: any[]) => void, delay = 100) => {
  let timer: any;
  return (...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

type Props = {
  open: boolean;
  onClose: () => void;
  offer: GroupedOffer;
  onOffersUpdated: (updated: { offerId: string; price: number }[]) => void;
  onOffersDeleted: (ids: string[]) => void;
};

const OfferEditModal: React.FC<Props> = ({
                                           open,
                                           onClose,
                                           offer,
                                           onOffersUpdated,
                                           onOffersDeleted,
                                         }) => {
  const maxQuantity = offer.offers.length;
  const basePrice = offer.price;

  const [localQuantity, setLocalQuantity] = useState(maxQuantity);
  const [localPrice, setLocalPrice] = useState(basePrice);
  const [loading, setLoading] = useState(false);

  const debouncedPriceUpdate = useMemo(
      () => debounce((p: number) => setLocalPrice(p), 50),
      []
  );

  const debouncedQtyUpdate = useMemo(
      () => debounce((q: number) => setLocalQuantity(q), 50),
      []
  );

  const handleSave = async () => {
    setLoading(true);

    try {
      const keepIds = offer.offers.slice(0, localQuantity).map((o) => o.id);
      const removeIds = offer.offers.slice(localQuantity).map((o) => o.id);

      // ✅ DELETE
      if (removeIds.length > 0) {
        await api.post(`/on-sale/remove-offers`, removeIds);
        onOffersDeleted(removeIds);
      }

      // ✅ UPDATE PRICE
      if (localPrice !== basePrice && keepIds.length > 0) {
        const payload = keepIds.map((id) => ({
          offerId: id,
          steamAccountId: offer.steamAccount.id,
          price: localPrice,
          isPublic: true,
        }));

        await api.put(`/on-sale/update-offers`, payload);

        onOffersUpdated(
            keepIds.map((id) => ({
              offerId: id,
              price: localPrice,
            }))
        );
      }

      toast.success("Offer updated");
      onClose();
    } catch {
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  const img = offer.item.catalog.imageLink ?? "";
  const name = offer.item.catalog.marketHashName ?? "Unknown Item";

  return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <div className="relative">
          <DialogTitle>Edit Offer</DialogTitle>
          <IconButton onClick={onClose} className="!absolute right-3 top-3">
            <CloseIcon />
          </IconButton>
        </div>

        <DialogContent className="flex flex-col gap-4">
          {/* HEADER */}
          <div className="flex items-center gap-3">
            {img ? (
                <img src={img} alt={name} className="w-16 h-16 object-contain" />
            ) : (
                <div className="w-16 h-16 bg-neutral-700" />
            )}

            <div className="flex flex-col flex-1">
              <span className="text-white font-semibold text-sm">{name}</span>
              <span className="text-gray-400 text-xs">
              Max Quantity: {maxQuantity}
            </span>
            </div>

            <TextField
                value={localPrice}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  setLocalPrice(val);
                  debouncedPriceUpdate(val);
                }}
                type="number"
                size="small"
                InputProps={{
                  endAdornment: <span className="text-gray-400 ml-1">€</span>,
                }}
                sx={{ width: 120 }}
            />
          </div>

          {/* QUANTITY SLIDER */}
          {maxQuantity > 1 && (
              <div className="flex items-center gap-3 mt-2">
                <Slider
                    value={localQuantity}
                    min={1}
                    max={maxQuantity}
                    step={1}
                    onChange={(_, v) => {
                      const qty = Array.isArray(v) ? v[0] : (v as number);
                      setLocalQuantity(qty);
                      debouncedQtyUpdate(qty);
                    }}
                    sx={{
                      flexGrow: 1,
                      height: 8,
                      '& .MuiSlider-thumb': {
                        width: 16,
                        height: 16,
                        backgroundColor: '#fff',
                      },
                      '& .MuiSlider-track': { height: 8 },
                      '& .MuiSlider-rail': { height: 8, opacity: 0.4 },
                    }}
                />

                <span className="text-gray-300 w-12 text-right text-sm">
              x {localQuantity}
            </span>
              </div>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} className="text-gray-700 dark:text-gray-200">
            Cancel
          </Button>
          <Button
              onClick={handleSave}
              disabled={loading}
              variant="contained"
              color="warning"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
  );
};

export default OfferEditModal;
